import { z } from 'zod';
import { capabilitySchema } from './capabilities.js';
import { issuedInvitationSchema } from './invitations.js';
import { displayNameSchema, tenantKindSchema, tenantStatusSchema } from './tenancy.js';

export const USER_STATUSES = ['ACTIVE', 'DISABLED'] as const;
export const userStatusSchema = z.enum(USER_STATUSES);
export type UserStatus = z.infer<typeof userStatusSchema>;

/** CUSTOMER sessions arrive with end customers (Phase 6). */
export const SESSION_REALMS = ['STAFF'] as const;
export const sessionRealmSchema = z.enum(SESSION_REALMS);
export type SessionRealm = z.infer<typeof sessionRealmSchema>;

export const PLATFORM_ROLES = ['PLATFORM_OWNER', 'PLATFORM_OPERATOR', 'PLATFORM_SUPPORT'] as const;
export const platformRoleSchema = z.enum(PLATFORM_ROLES);
export type PlatformRole = z.infer<typeof platformRoleSchema>;

export const TENANT_ROLES = ['TENANT_OWNER', 'TENANT_ADMIN', 'TENANT_STAFF'] as const;
export const tenantRoleSchema = z.enum(TENANT_ROLES);
export type TenantRole = z.infer<typeof tenantRoleSchema>;

/** Every membership role. Platform roles need a NULL tenant; tenant roles need a tenant. */
export const STAFF_ROLES = [...PLATFORM_ROLES, ...TENANT_ROLES] as const;
export const staffRoleSchema = z.enum(STAFF_ROLES);
export type StaffRole = z.infer<typeof staffRoleSchema>;

export function isPlatformRole(role: StaffRole): role is PlatformRole {
  return (PLATFORM_ROLES as readonly string[]).includes(role);
}

export function isTenantRole(role: StaffRole): role is TenantRole {
  return (TENANT_ROLES as readonly string[]).includes(role);
}

export const emailSchema = z.email().trim().toLowerCase().max(254);

/** The password policy for staff and shoppers alike (enforced by the hasher in @avrahami/auth). */
export const MIN_PASSWORD_LENGTH = 10;
/** Upper bound protects the hasher from multi-megabyte inputs. */
export const MAX_PASSWORD_LENGTH = 256;

export const loginRequestSchema = z.object({
  // Not validated as an email: a malformed value is just another failed login (no oracle).
  email: z.string().trim().toLowerCase().min(1).max(254),
  password: z.string().min(1).max(256),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const switchTenantRequestSchema = z.object({ tenantId: z.uuid() });

export const changePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1).max(256),
  newPassword: z.string().min(10).max(256),
});

export const tenantMembershipSummarySchema = z.object({
  tenantId: z.uuid(),
  slug: z.string(),
  displayName: z.string(),
  kind: tenantKindSchema,
  status: tenantStatusSchema,
  role: tenantRoleSchema,
});
export type TenantMembershipSummary = z.infer<typeof tenantMembershipSummarySchema>;

export const meResponseSchema = z.object({
  user: z.object({ id: z.uuid(), email: z.string(), displayName: z.string() }),
  platform: z
    .object({ role: platformRoleSchema, capabilities: z.array(capabilitySchema) })
    .nullable(),
  memberships: z.array(tenantMembershipSummarySchema),
  activeTenant: tenantMembershipSummarySchema
    .extend({
      capabilities: z.array(capabilitySchema),
      /**
       * The signed-in person is not a member of this store: they are platform staff managing it
       * as its owner would (adr/0031).
       */
      managedByPlatform: z.boolean(),
    })
    .nullable(),
  mfa: z.object({
    enabled: z.boolean(),
    /** True for platform staff: the platform surface refuses them until they enroll. */
    required: z.boolean(),
  }),
});
export type MeResponse = z.infer<typeof meResponseSchema>;

/**
 * The way into a store's own admin for platform staff (adr/0031): a token that the store's admin
 * exchanges, once and within a minute, for a session that manages that store.
 */
export const storeEntrySchema = z.object({
  token: z.string(),
  expiresAt: z.iso.datetime(),
});
export type StoreEntry = z.infer<typeof storeEntrySchema>;

export const storeEntryRequestSchema = z.object({ token: z.string().min(1).max(100) });
export type StoreEntryRequest = z.infer<typeof storeEntryRequestSchema>;

/**
 * The stores this browser has a session of in the tenant admin (adr/0033). Ids only: the sign-in
 * screen never shows who is signed in where, so a shared computer does not tell the next person.
 */
export const storeSessionsResponseSchema = z.object({
  stores: z.array(z.object({ tenantId: z.uuid() })),
});
export type StoreSessionsResponse = z.infer<typeof storeSessionsResponseSchema>;

export const LOGIN_STATUSES = ['SIGNED_IN', 'MFA_REQUIRED'] as const;

/**
 * A correct password either signs the user in, or (when they have a second factor) opens a
 * short pending session that only `POST /v1/auth/mfa/verify` can complete.
 */
export const loginResponseSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('SIGNED_IN'), me: meResponseSchema }),
  z.object({
    status: z.literal('MFA_REQUIRED'),
    /** Development only (`MFA_DEV_CODES`): the code to type, instead of an authenticator app. */
    devCode: z.string().optional(),
  }),
]);
export type LoginResponse = z.infer<typeof loginResponseSchema>;

/** A member of the current tenant, as tenant admins see it. */
export const membershipSchema = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  email: z.string(),
  displayName: z.string(),
  role: tenantRoleSchema,
  /** True until the person sets a password through an invitation link. */
  needsPasswordSetup: z.boolean(),
  /** When the newest open invitation expires; null when none is open. */
  invitationExpiresAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type Membership = z.infer<typeof membershipSchema>;

export const updateMembershipRequestSchema = z.object({ role: tenantRoleSchema });

export const userIdParamsSchema = z.object({ userId: z.uuid() });

/** A member of Avrahami's platform staff, as platform owners see it. */
export const platformStaffMemberSchema = z.object({
  userId: z.uuid(),
  email: z.string(),
  displayName: z.string(),
  role: platformRoleSchema,
  status: userStatusSchema,
  needsPasswordSetup: z.boolean(),
  mfaEnabled: z.boolean(),
  /** When the newest open invitation expires; null when none is open. */
  invitationExpiresAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type PlatformStaffMember = z.infer<typeof platformStaffMemberSchema>;

export const invitePlatformStaffRequestSchema = z.object({
  email: emailSchema,
  displayName: displayNameSchema,
  role: platformRoleSchema,
});
export type InvitePlatformStaffRequest = z.infer<typeof invitePlatformStaffRequestSchema>;

export const invitePlatformStaffResponseSchema = z.object({
  member: platformStaffMemberSchema,
  /** Null when the person already has a password: they simply sign in. */
  invitation: issuedInvitationSchema.nullable(),
});
export type InvitePlatformStaffResponse = z.infer<typeof invitePlatformStaffResponseSchema>;

export const updatePlatformStaffRequestSchema = z.object({ role: platformRoleSchema });
